/* ═══════════════════════════════════════════════════════════════════════════
   ☎️ GuestCall — one phone call with somebody who does not have Allo.

   A call link drops a stranger into three steps: say who you are, check your
   camera and microphone actually work, then ring the person who sent it.

   ── 🟢 THE CALL ENGINE IS THE ONE THE WEB APP ALREADY USES ─────────────────
   services/calls.js speaks the phones' protocol verbatim and is not gated on
   being signed in — it takes a uid and a socket and nothing else. So the guest
   gets voice→video upgrade, mute, camera toggle, ICE restart, the quality log
   and the ringback cadence for free, and there is exactly ONE call engine in
   this codebase rather than a second one that drifts.

   ── 🟥 THE GUEST CALLS THE OWNER, NEVER THE OTHER WAY ROUND ────────────────
   A browser holding a link has no push token and no way to be woken: there is
   no CallKit, no FCM, nothing to ring. If the owner could dial the guest, the
   call would simply never arrive whenever the tab was not already open and
   focused. The guest dialling out is not a limitation of the design, it IS the
   design — and it means the owner's phone rings through the ordinary incoming
   call path, CallKit and all, with no new server work.

   ── 🟥 A PREVIEW BEFORE THE CALL, NOT AFTER ────────────────────────────────
   The person on the other end is about to be rung on their phone. Discovering
   the camera is pointed at the ceiling, or that the browser never granted the
   microphone, is a thing to find out BEFORE somebody picks up — not during the
   first four seconds of a call that then has to be abandoned and re-made from a
   link that may already be spent.
   ═══════════════════════════════════════════════════════════════════════════ */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import SafeImg from './SafeImg';
import { Phone, Video, Timer, Lock, MicOn, MicOff, CamOff, HangIcon, FlipCamera } from './icons';
import {
  attachCalls, startCall, endCall, subscribeCalls, getCallState, clearLastEndedCall,
  toggleMute, toggleCamera, requestVideoUpgrade, acceptVideoUpgrade, declineVideoUpgrade,
  flipCamera, hasMultipleCameras, fetchIce,
} from '../services/calls';
import CallRatingCard from './CallRatingCard';
import { initSocket, getSocket, closeSocket } from '../services/socket';

/* ── how long is left on the link ─────────────────────────────────────────── */
function leftLabel(ms) {
  const d = Math.max(0, Number(ms) - Date.now());
  const h = Math.floor(d / 3600e3);
  if (h >= 24) return `${Math.floor(h / 24)} days`;
  if (h >= 1) return `${h} hour${h === 1 ? '' : 's'}`;
  return `${Math.max(1, Math.floor(d / 60e3))} minutes`;
}

const clock = (startedAt) => {
  const s = Math.max(0, Math.floor((Date.now() - Number(startedAt || 0)) / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

/* ══ STEP 1 — who are you, and what kind of call ═══════════════════════════ */
function CallJoinScreen({ link, onContinue, busy, error }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [passcode, setPasscode] = useState('');
  const [callType, setCallType] = useState('voice');

  const ready = name.trim().length > 1
    && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())
    && (!link.needsPasscode || passcode.length > 0);

  return (
    <div className="gs-join">
      <div className="gs-join-card">
        <div className="gs-join-who">
          {link.ownerImage
            ? <SafeImg src={link.ownerImage} kind="inline" className="gs-join-ava" />
            : <span className="gs-join-ava gs-ph">{(link.ownerName || '?').charAt(0).toUpperCase()}</span>}
          <b>{link.ownerName}</b>
          <i>invited you to call them on Allo</i>
        </div>

        <div className="gs-temp-note">
          <Timer width={15} height={15} />
          <span>
            This link is for <b>one call</b>. It works for the next{' '}
            <b>{leftLabel(link.expiresAt)}</b>, and stops working once you have spoken.
          </span>
        </div>

        <label className="gs-field">
          <span>Your name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Kelly" maxLength={40} autoFocus />
          <em>So they know who is calling before they pick up.</em>
        </label>
        <label className="gs-field">
          <span>Email</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kelly@example.com" type="email" maxLength={120} />
        </label>

        {link.needsPasscode && (
          <label className="gs-field">
            <span>Passcode</span>
            <input value={passcode} onChange={(e) => setPasscode(e.target.value)} placeholder="••••" type="password" />
          </label>
        )}

        <div className="gs-field">
          <span>How do you want to call?</span>
          <div className="gc-kind">
            <button
              type="button"
              className={`gc-kind-btn ${callType === 'voice' ? 'on' : ''}`}
              onClick={() => setCallType('voice')}
            >
              <Phone width={17} height={17} /> Voice
            </button>
            <button
              type="button"
              className={`gc-kind-btn ${callType === 'video' ? 'on' : ''}`}
              onClick={() => setCallType('video')}
            >
              <Video width={17} height={17} /> Video
            </button>
          </div>
          {/* 🟢 say the upgrade exists here, so choosing voice is not a decision
              somebody feels they have to get right */}
          <em>You can switch to video during the call.</em>
        </div>

        {error && <div className="gs-err">{error}</div>}

        <button
          className="gs-go" disabled={!ready || busy}
          onClick={() => onContinue({ name: name.trim(), email: email.trim(), passcode, callType })}
        >
          {busy ? 'One moment…' : 'Continue'}
        </button>

        <p className="gs-fine">
          <Lock width={12} height={12} /> The call itself is encrypted directly between the
          two of you. Allo&rsquo;s servers only connect it.
        </p>
      </div>
    </div>
  );
}

/* ══ STEP 2 — does any of this actually work ══════════════════════════════ */
function CallPreview({ session, callType, onJoin, onBack, joining }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [level, setLevel] = useState(0);
  const [denied, setDenied] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let dead = false;
    let ctx = null;
    let raf = 0;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: callType === 'video'
            ? { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } }
            : false,
        });
        if (dead) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setReady(true);

        /* 🟢 A LIVE MIC METER, NOT JUST A PERMISSION PROMPT. "Allowed" and
           "working" are different things — a muted hardware switch, the wrong
           default input, or a headset that never connected all pass the
           permission check and produce silence. Seeing the bar move is the only
           thing that actually answers the question. */
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          ctx = new AC();
          const src = ctx.createMediaStreamSource(stream);
          const an = ctx.createAnalyser();
          an.fftSize = 512;
          src.connect(an);
          const buf = new Uint8Array(an.frequencyBinCount);
          const tick = () => {
            an.getByteTimeDomainData(buf);
            let peak = 0;
            for (let i = 0; i < buf.length; i += 1) peak = Math.max(peak, Math.abs(buf[i] - 128));
            setLevel(Math.min(1, peak / 40));
            raf = requestAnimationFrame(tick);
          };
          tick();
        }
      } catch (e) {
        if (dead) return;
        /* 🟥 THE THREE FAILURES READ DIFFERENTLY AND NEED DIFFERENT ADVICE.
           "Something went wrong" sends somebody to reload the page, which fixes
           none of them. */
        const n = e && e.name;
        setDenied(
          n === 'NotAllowedError'
            ? `Your browser blocked the ${callType === 'video' ? 'camera and microphone' : 'microphone'}. Allow it in the address bar, then try again.`
            : n === 'NotFoundError'
              ? `No ${callType === 'video' ? 'camera or microphone' : 'microphone'} was found on this device.`
              : n === 'NotReadableError'
                ? 'Another app is already using it. Close that, then try again.'
                : 'Could not start your camera or microphone.',
        );
      }
    })();

    return () => {
      dead = true;
      if (raf) cancelAnimationFrame(raf);
      try { ctx && ctx.close(); } catch { /* already closed */ }
      /* 🟥 STOP THE TRACKS. A preview stream left running keeps the camera light
         on through the whole call and holds the device on some machines, so the
         call's own getUserMedia then fails with NotReadableError — the preview
         breaking the thing it exists to make work. */
      const s = streamRef.current;
      if (s) s.getTracks().forEach((t) => { try { t.stop(); } catch { /* gone */ } });
      streamRef.current = null;
    };
  }, [callType]);

  return (
    <div className="gs-join">
      <div className="gs-join-card gc-preview-card">
        <div className="gs-join-who">
          {session.ownerImage
            ? <SafeImg src={session.ownerImage} kind="inline" className="gs-join-ava" />
            : <span className="gs-join-ava gs-ph">{(session.ownerName || '?').charAt(0).toUpperCase()}</span>}
          <b>Calling {session.ownerName}</b>
          <i>{callType === 'video' ? 'Video call' : 'Voice call'}</i>
        </div>

        {callType === 'video' ? (
          <div className="gc-preview">
            {/* 🟥 MUTED, and it has to be. A preview that plays your own
                microphone back through your own speakers is a feedback loop. */}
            <video ref={videoRef} autoPlay playsInline muted className="gc-preview-vid" />
            {!ready && !denied && <div className="gc-preview-wait">Starting your camera…</div>}
          </div>
        ) : (
          <div className="gc-voice-check">
            <MicOn width={26} height={26} />
            <span>{ready ? 'Say something — the bar should move.' : 'Starting your microphone…'}</span>
          </div>
        )}

        <div className="gc-meter" aria-hidden="true">
          <i style={{ width: `${Math.round(level * 100)}%` }} />
        </div>

        {denied && <div className="gs-err">{denied}</div>}

        <button className="gs-go" disabled={!ready || joining} onClick={onJoin}>
          {joining ? 'Calling…' : `Join call`}
        </button>
        <button className="gc-back" type="button" onClick={onBack} disabled={joining}>
          Back
        </button>
      </div>
    </div>
  );
}

/* ══ STEP 3 — the call itself ═════════════════════════════════════════════
   ── 🟥 BOTH VIDEO ELEMENTS STAY MOUNTED, ALWAYS ───────────────────────────
   The first version of this screen rendered the remote <video> only while the
   peer's camera was on, and the self-view only while ours was. That is the
   obvious way to write it and it produces a bug that looks like a WebRTC
   failure: turn a camera off and on again and the picture never comes back.

   Nothing is wrong with the media. React unmounted the element, and the
   effect that assigns `srcObject` was keyed on the STREAM — which never
   changed identity, because a camera toggle swaps a track inside the same
   MediaStream. So the new element mounted with `srcObject` still null and sat
   there black forever, on both sides of the call.

   So: both elements are mounted for the whole call and hidden with CSS, and
   the binding effect runs after EVERY render and re-attaches whenever what the
   element holds is not what it should hold. A cheap identity check beats a
   dependency array that has to be right about a mutable object.

   ── 🟢 EITHER PICTURE CAN BE THE BIG ONE ──────────────────────────────────
   Tapping either surface swaps them, so `swapped` decides which STREAM goes to
   which ELEMENT — the elements themselves never move. That is what keeps the
   swap free: no remount, no reattach, no flicker.
   ═══════════════════════════════════════════════════════════════════════════ */

/** How far the picture-in-picture sits from the edges it snaps to. */
const PIP_MARGIN = 14;
/** Seconds of stillness before the controls fade out on a video call. */
const IDLE_MS = 5000;
/** Under this much movement a pointer gesture was a tap, not a drag. */
export const TAP_SLOP = 6;

/**
 * Where the picture-in-picture comes to rest when a drag ends.
 *
 * 🟥 SNAP SIDEWAYS, KEEP THE HEIGHT THEY CHOSE. Both edges are proper homes for
 *    this thing — being able to move it off the face in the bottom-right corner
 *    is the whole point — but a pip left floating in the middle covers the very
 *    picture it is sitting on. Horizontal position is a choice between two
 *    answers; vertical position is a preference, and is left alone.
 *
 * 🟢 Pure, and exported, because the interesting part of a drag is where it
 *    ENDS — and that is a calculation, not a gesture.
 */
export function snapPip(pos, box, margin = PIP_MARGIN) {
  if (!pos || !box) return pos;
  const maxX = Math.max(margin, box.sw - box.pw - margin);
  const maxY = Math.max(margin, box.sh - box.ph - margin);
  const centre = pos.x + box.pw / 2;
  return {
    x: centre < box.sw / 2 ? margin : maxX,
    y: Math.min(Math.max(margin, pos.y), maxY),
  };
}

/** A gesture that went nowhere was a tap. */
export const isTap = (moved) => moved < TAP_SLOP;

function CallStage({ session, onEnded }) {
  const [call, setCall] = useState(getCallState());
  const [, tick] = useState(0);
  const stageRef = useRef(null);
  /* 🟥 THE PIP'S WORLD IS THE PICTURE, NOT THE WHOLE SCREEN. It is positioned
     absolutely inside .gc-remote, so that is the box its coordinates are in —
     and .gc-remote is shorter than the stage by the height of the controls.
     Measuring against the stage let the drag clamp put the pip UNDER the
     controls, where it would be half off the bottom of the picture. */
  const surfaceRef = useRef(null);
  /* 🟥 THE CONTROLS ARE MEASURED, NOT GUESSED. Now that they float ON the
     picture rather than beside it, the picture no longer shrinks to make room —
     so nothing stops the pip being dropped underneath them, where it is both
     covered and no longer grabbable. Reading the pill's real height keeps that
     area out of the drag's reach without hard-coding a number that changes with
     the safe-area inset and the button count. */
  const controlsRef = useRef(null);
  const bigRef = useRef(null);
  /* two refs, deliberately: the BOX is what gets measured and dragged, the
     VIDEO inside it is what gets a stream bound to it */
  const pipBoxRef = useRef(null);
  const pipRef = useRef(null);

  /* which stream is in the big surface */
  const [swapped, setSwapped] = useState(false);
  /* the controls have faded out and the pip has shrunk away */
  const [idle, setIdle] = useState(false);
  const [wokeAt, setWokeAt] = useState(0);
  /* the pip's position, in px from the stage's top-left; null until measured */
  const [pip, setPip] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [canFlip, setCanFlip] = useState(false);
  const drag = useRef(null);

  useEffect(() => subscribeCalls(setCall), []);

  /* the in-call timer has to move on its own — nothing else re-renders once a
     call has settled */
  useEffect(() => {
    if (call.status !== 'active') return undefined;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [call.status]);

  /* 🟢 ASK THE DEVICE WHETHER A FLIP IS EVEN POSSIBLE. A laptop with one webcam
     gets no flip button rather than one that visibly does nothing. */
  useEffect(() => {
    let live = true;
    hasMultipleCameras().then((yes) => { if (live) setCanFlip(!!yes); }).catch(() => {});
    return () => { live = false; };
  }, []);

  /* 🟥 THE CALL ENDING IS THE END OF THE GUEST'S SESSION. There is no chat to
     fall back to and the link is probably spent, so 'idle' means we are done —
     and the parent needs to know so it can show what happened rather than a
     blank card. */
  const wasLive = useRef(false);
  useEffect(() => {
    if (call.status !== 'idle') { wasLive.current = true; return; }
    if (wasLive.current) onEnded(call);
  }, [call.status, onEnded, call]);

  const video = call.callType === 'video';
  const remoteLive = !!call.remoteStream && !call.peerCameraOff && !call.peerVideoHeld;   // 📶 a held video is their face too (r117)
  const localLive = !!call.localStream && !call.cameraOff;

  /* what goes where — the STREAMS move, the elements never do */
  const bigStream = swapped ? call.localStream : call.remoteStream;
  const pipStream = swapped ? call.remoteStream : call.localStream;
  const bigIsLocal = swapped;
  const bigLive = video && (swapped ? localLive : remoteLive);
  const pipLive = video && (swapped ? remoteLive : localLive);

  /* ── 🟥 bind after EVERY render, and only when it is actually wrong ──────
     No dependency array on purpose. The streams are mutable objects whose
     identity survives a camera toggle, so any dep list that watches them is a
     list that misses the case this exists for. The `!==` guard makes the extra
     runs free, and `play()` is only nudged on an actual reattach. */
  useEffect(() => {
    const bind = (el, stream, mute) => {
      if (!el) return;
      /* 🟥 SET `muted` AS A PROPERTY. React writes the initial attribute and then
         leaves it alone, so a swap that moves our own microphone into the big
         element would put us through our own speakers — the feedback loop the
         preview screen went to some trouble to avoid. */
      el.muted = !!mute;
      if (stream && el.srcObject !== stream) {
        el.srcObject = stream;
        const p = el.play && el.play();
        if (p && p.catch) p.catch(() => { /* autoplay policy; the element still shows frames */ });
      } else if (!stream && el.srcObject) {
        el.srcObject = null;
      }
    };
    bind(bigRef.current, bigStream, bigIsLocal);
    bind(pipRef.current, pipStream, !bigIsLocal);
  });

  /* ── the pip's resting place ──────────────────────────────────────────── */
  const pipBox = useCallback(() => {
    const stage = surfaceRef.current;
    const el = pipBoxRef.current;
    if (!stage || !el) return null;
    const s = stage.getBoundingClientRect();
    const p = el.getBoundingClientRect();
    const c = controlsRef.current ? controlsRef.current.getBoundingClientRect() : null;
    /* 🟢 MEASURED WHILE FADED TOO. The pill keeps its size when it is invisible,
       so the reachable area does not jump the moment the controls hide — a pip
       that shifted every five seconds on its own would be worse than one that
       simply avoids the corner. */
    const reserved = c ? c.height + PIP_MARGIN * 2 : 0;
    return { sw: s.width, sh: Math.max(p.height + PIP_MARGIN * 2, s.height - reserved), pw: p.width, ph: p.height };
  }, []);

  /* start bottom-right, and stay inside the stage when the window changes */
  useEffect(() => {
    const place = () => {
      const b = pipBox();
      if (!b) return;
      setPip((cur) => {
        const maxX = Math.max(PIP_MARGIN, b.sw - b.pw - PIP_MARGIN);
        const maxY = Math.max(PIP_MARGIN, b.sh - b.ph - PIP_MARGIN);
        if (!cur) return { x: maxX, y: maxY };
        return { x: Math.min(cur.x, maxX), y: Math.min(cur.y, maxY) };
      });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [pipBox, video]);

  /* ── 🟢 the controls fade, and the pip gets out of the way with them ────
     Only on a VIDEO call: on a voice call there is nothing behind the controls
     to reveal, so hiding them would leave a person staring at an avatar with no
     way to hang up until they guessed to tap. And never while the peer is
     waiting on an answer about video, or before the call is up — a question and
     a progress note are exactly what somebody is looking at. */
  const canHide = video && call.status === 'active' && !call.upgradeAsk;
  useEffect(() => {
    if (!canHide) { setIdle(false); return undefined; }
    const id = setTimeout(() => setIdle(true), IDLE_MS);
    return () => clearTimeout(id);
  }, [canHide, wokeAt]);

  /* 🟥 THROTTLED, BECAUSE IT IS ON pointermove. An unthrottled wake would call
     setState for every pixel of every drag — a render storm underneath a
     gesture that has to stay smooth. Twice a second is far more resolution than
     a five-second timer needs. */
  const lastWake = useRef(0);
  const wake = useCallback(() => {
    const now = Date.now();
    if (now - lastWake.current < 400) return;
    lastWake.current = now;
    setIdle(false);
    setWokeAt(now);
  }, []);

  /* ── dragging the pip, either side, smoothly ───────────────────────────
     🟥 POINTER EVENTS WITH CAPTURE, not mouse+touch pairs. One code path for a
     finger, a mouse and a pen, and the capture is what stops the drag dying the
     moment the pointer leaves the little box — which, since the box is small and
     fingers are not, is most drags. */
  const onPipDown = (e) => {
    if (!pip) return;
    wake();
    const b = pipBox();
    drag.current = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: pip.x,
      originY: pip.y,
      moved: 0,
      box: b,
    };
    setDragging(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* older engines */ }
  };

  const onPipMove = (e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    d.moved = Math.max(d.moved, Math.abs(dx) + Math.abs(dy));
    const b = d.box;
    if (!b) return;
    const maxX = Math.max(0, b.sw - b.pw);
    const maxY = Math.max(0, b.sh - b.ph);
    setPip({
      x: Math.min(Math.max(0, d.originX + dx), maxX),
      y: Math.min(Math.max(0, d.originY + dy), maxY),
    });
  };

  const onPipUp = (e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    setDragging(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already gone */ }

    /* 🟢 A GESTURE THAT WENT NOWHERE IS A TAP. Without this the swap would only
       fire on a perfectly still finger, and a tap that moves three pixels — every
       real tap — would do nothing at all. */
    if (isTap(d.moved)) { setSwapped((v) => !v); return; }

    const b = d.box;
    if (!b) return;
    setPip((cur) => snapPip(cur, b));
  };

  const pipStyle = pip ? {
    transform: `translate3d(${pip.x}px, ${pip.y + (idle ? 26 : 0)}px, 0) scale(${idle ? 0.78 : 1})`,
    transition: dragging ? 'none' : 'transform .26s cubic-bezier(.22,.61,.36,1)',
  } : { opacity: 0 };

  /* 🟥 MIRROR THE FRONT CAMERA ONLY, and only when the picture IS our camera.
     A mirrored rear camera renders every sign and every face the wrong way
     round, and a mirrored view of the person we called is simply wrong. */
  const mirror = (isLocal) => (isLocal && call.facing !== 'environment' ? ' mirror' : '');

  const peerName = session.ownerName;
  const showAvatar = !bigLive;

  return (
    <div
      className={`gc-stage ${video ? 'video' : 'voice'}${idle ? ' idle' : ''}`}
      ref={stageRef}
      onPointerDown={wake}
      onPointerMove={wake}
    >
      <div className="gc-remote" ref={surfaceRef} onClick={() => { wake(); if (video) setSwapped((v) => !v); }}>
        {/* the big picture — mounted for the whole call, hidden when there is
            nothing to show, never unmounted */}
        <video
          ref={bigRef}
          autoPlay
          playsInline
          className={`gc-remote-vid${showAvatar ? ' hide' : ''}${mirror(bigIsLocal)}`}
        />

        {showAvatar && (
          <div className="gc-remote-ph">
            {session.ownerImage
              ? <SafeImg src={session.ownerImage} kind="inline" className="gc-remote-ava" />
              : <span className="gc-remote-ava gs-ph">{(peerName || '?').charAt(0).toUpperCase()}</span>}
            <b>{peerName}</b>
            <i>
              {call.status === 'active'
                ? (call.peerVideoHeld && video && !swapped ? 'Video paused · Poor connection'
                  : call.peerCameraOff && video && !swapped ? 'Camera off'
                  : swapped && call.cameraOff ? 'Your camera is off'
                    : clock(call.startedAt))
                : (call.note || 'Connecting…')}
            </i>
          </div>
        )}

        {/* 🟥 THE PEER'S MUTE IS AN OVERLAY, NOT PART OF THE AVATAR BLOCK. It used
            to live inside the placeholder above, which meant it appeared on a
            voice call and was invisible for the whole of every video call — so
            the guest could not tell the difference between the other person
            being muted and the other person saying nothing. */}
        {call.peerMuted && call.status === 'active' && (
          <div className="gc-badge muted"><MicOff width={13} height={13} /> {peerName} is muted</div>
        )}

        {call.status === 'active' && !showAvatar && (
          <div className="gc-overlay-name">{peerName} · {clock(call.startedAt)}</div>
        )}

        {/* our own picture, small and movable */}
        <div
          className={`gc-pip${dragging ? ' dragging' : ''}${pipLive ? '' : ' empty'}`}
          ref={pipBoxRef}
          style={pipStyle}
          onPointerDown={onPipDown}
          onPointerMove={onPipMove}
          onPointerUp={onPipUp}
          onPointerCancel={onPipUp}
          /* 🟥 THE PIP SITS INSIDE THE BIG SURFACE, so a tap on it also reaches
             the big surface's own handler — which would swap a second time and
             land exactly where it started. The pip decides its own taps. */
          onClick={(e) => e.stopPropagation()}
        >
          <video
            ref={pipRef}
            autoPlay
            playsInline
            className={`gc-pip-vid${pipLive ? '' : ' hide'}${mirror(!bigIsLocal)}`}
          />
          {!pipLive && (
            <span className="gc-pip-off"><CamOff width={16} height={16} /></span>
          )}
        </div>
      </div>

      {/* the peer asked to turn this into a video call */}
      {call.upgradeAsk && (
        <div className="gc-ask">
          <span>{peerName} wants to switch to video</span>
          <button className="gc-ask-yes" onClick={() => acceptVideoUpgrade()}>Turn on camera</button>
          <button className="gc-ask-no" onClick={() => declineVideoUpgrade()}>Keep voice</button>
        </div>
      )}

      <div className="gc-controls" ref={controlsRef}>
        <button
          className={`gc-btn ${call.muted ? 'off' : ''}`}
          onClick={() => toggleMute()}
          title={call.muted ? 'Unmute' : 'Mute'}
        >
          {call.muted ? <MicOff width={20} height={20} /> : <MicOn width={20} height={20} />}
        </button>

        {video ? (
          <>
            <button
              className={`gc-btn ${call.cameraOff ? 'off' : ''}`}
              onClick={() => toggleCamera()}
              title={call.cameraOff ? 'Turn camera on' : 'Turn camera off'}
            >
              {call.cameraOff ? <CamOff width={20} height={20} /> : <Video width={20} height={20} />}
            </button>
            {canFlip && (
              <button
                className="gc-btn"
                /* 🟢 nothing to flip while the camera is off — the button would
                   open the other camera and send nothing */
                disabled={call.cameraOff}
                onClick={() => flipCamera()}
                title="Switch camera"
              >
                <FlipCamera width={20} height={20} />
              </button>
            )}
          </>
        ) : (
          /* 🟢 the same upgrade the app offers — a guest who chose voice is not
             stuck with it, which is why the join screen says so up front */
          <button
            className="gc-btn"
            disabled={call.status !== 'active' || call.upgradePending}
            onClick={() => requestVideoUpgrade()}
            title="Switch to video"
          >
            <Video width={20} height={20} />
          </button>
        )}

        <button className="gc-btn hang" onClick={() => endCall('local')} title="End call">
          <HangIcon width={20} height={20} />
        </button>
      </div>

      {call.upgradePending && <div className="gc-note">Waiting for them to turn their camera on…</div>}
    </div>
  );
}


/* ══ the whole guest call flow ════════════════════════════════════════════ */
/* 🧾 M3: what the end screen says — never a raw engine code ("failed", "mic_denied") */
const SPENT_CODES = new Set(['link_used']);
function endTitle(e) {
  if (!e) return 'Call ended';
  if (e.verdict === 'answered') return 'Call ended';
  if (e.verdict === 'busy') return 'Busy';
  if (e.verdict === 'declined') return 'They couldn\u2019t take the call';
  if (e.verdict === 'no_answer') return 'No answer';
  if (String(e.error).startsWith('link_')) return 'This link can\u2019t make calls';
  return 'The call didn\u2019t connect';
}
function endCopy(e, name) {
  const who = name || 'them';
  if (!e || e.verdict === 'answered') return `Your call with ${who} has ended. This link was for one call, so it will not work again \u2014 ask for a new one if you need to talk.`;
  if (e.error === 'link_used') return 'This link has already been used for a call. Ask for a new one.';
  if (e.error === 'link_expired') return 'This link has expired. Ask for a new one.';
  if (String(e.error).startsWith('link_')) return 'This link is no longer active. Ask for a new one.';
  if (e.error === 'mic_denied') return 'Allow microphone access for this site in your browser, then try again.';
  if (e.error === 'camera_denied') return 'Allow camera access for this site in your browser, then try again.';
  if (e.verdict === 'busy') return `${who} is on another call. You can try again in a moment.`;
  if (e.verdict === 'declined') return `${who} couldn\u2019t answer right now. You can try again.`;
  if (e.verdict === 'no_answer') return `${who} didn\u2019t answer. You can try again.`;
  return 'The call couldn\u2019t connect. Check your connection and try again \u2014 this link still works.';
}
const canCallAgain = (e) => !!e && e.verdict !== 'answered' && !SPENT_CODES.has(e.error) && !String(e.error || '').startsWith('link_');

export default function GuestCall({ link, token }) {
  const [step, setStep] = useState('join');     // join | preview | calling | over
  const [session, setSession] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ending, setEnding] = useState(null);

  /* 🟥 M2: CLOSING THE TAB WHILE IT RANG RANG ON. The owner's phones kept ringing for a minute, and an
        answer then counted as a call. 🟢 Leaving the page ends the call it placed. */
  useEffect(() => {
    const leave = () => { try { endCall('cancelled'); } catch { /* nothing live */ } };
    window.addEventListener('pagehide', leave);
    return () => window.removeEventListener('pagehide', leave);
  }, []);

  /* 🟥 THE SOCKET AND THE CALL LISTENERS COME UP BEFORE THE CALL IS PLACED, and
     tearing them down is not optional. attachCalls binds handlers to the live
     socket; leaving them bound across a re-render would double-handle every
     event, and leaving the socket open after the call keeps this browser
     "present" to the server long after the person has gone. */
  useEffect(() => {
    if (!session || !session.guestUid) return undefined;
    let dead = false;
    (async () => {
      initSocket(session.guestUid, session.deviceId);
      if (dead) return;
      await attachCalls(session.guestUid);
    })();
    return () => { dead = true; try { closeSocket(); } catch { /* already gone */ } };
  }, [session]);

  const onContinue = async (f) => {
    setBusy(true); setError('');
    try {
      /* 🟢 JOIN FIRST, PREVIEW SECOND. The preview costs a camera permission
         prompt; finding out afterwards that the link was already spent would
         have asked somebody for their camera for nothing. */
      const { joinGuestCallLink } = await import('../services/guestSession');
      const s = await joinGuestCallLink({ token, ...f });
      setSession(s);
      setForm(f);
      setStep('preview');
      /* M9: the relay servers are fetched while they look at their preview, not at dial time */
      try { fetchIce().catch(() => {}); } catch { /* best effort */ }
    } catch (e) {
      setError(
        e.code === 'bad_passcode' ? 'That passcode is not right.'
          : e.code === 'already_used' ? 'This link has already been used for a call. Ask for a new one.'
            : e.code === 'expired' ? 'This link has expired.'
              : e.code === 'already_claimed' ? 'This link has already been used by someone else.'
                : e.code === 'account_exists' ? 'This browser is signed in to an Allo account. Open the link in a private window to join as a guest.'
                : e.code === 'not_a_call_link' ? 'This link is for a chat, not a call.'
                  : e.code === 'owner_cannot_join' || e.code === 'unauthenticated'
                    ? 'This link has to be opened as a guest. If you are signed in to Allo in this browser, open it in a private window instead.'
                    : e.code === 'email_invalid' ? 'Please check the email address.'
                      : 'Something went wrong. Try again.',
      );
    }
    setBusy(false);
  };

  const placeCall = useCallback(async () => {
    if (!session) return;
    setBusy(true); setError('');
    try {
      /* the socket needs a beat after initSocket — startCall's own guard says
         'offline' rather than waiting, so wait here */
      /* L3: wait for a CONNECTED socket, not just one that exists (a slow handshake read as "offline") */
      for (let i = 0; !(getSocket() && getSocket().connected) && i < 60; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 250));
      }
      setStep('calling');
      const ok = await startCall(session.ownerUid, form.callType === 'video', {
        uid: session.guestUid,
        fullName: session.me?.name || form.name,
        username: session.me?.name || form.name,
        profileImage: session.me?.image || null,
      });
      if (ok === false) {
        setStep('preview');
        setError('Could not start the call. Check your connection and try again.');
      }
    } catch (e) {
      setStep('preview');
      setError('Could not start the call. Try again.');
    }
    setBusy(false);
  }, [session, form]);

  /* 🟥 `lastEnded` IS READ HERE OR NOWHERE. teardown() in services/calls.js
     empties the call state — callId, peerUid, startedAt, the quality summary —
     and deliberately preserves exactly one field, `lastEnded`, for the rating
     card. This callback runs on the transition to 'idle', which is the only
     moment both "the call is over" and "the facts about it still exist" are
     true; reading it from state a render later would give a card about a call
     with no id, which the server rejects as bad_request. */
  const onEnded = useCallback((c) => {
    setEnding({
      error: (c && c.error) || '',
      verdict: (c && c.lastVerdict) || '',
      rate: (c && c.lastEnded) || null,
    });
    setStep('over');
  }, []);

  /* 🟢 THE CARD IS DISMISSED, THE CALL IS NOT UN-ENDED. Clearing only the `rate`
     half leaves the "call ended" message behind it standing, which is the thing
     the guest is actually left looking at. */
  const closeRating = useCallback(() => {
    try { clearLastEndedCall(); } catch { /* already cleared */ }
    setEnding((e) => (e ? { ...e, rate: null } : e));
  }, []);

  if (step === 'join') {
    return <CallJoinScreen link={link} onContinue={onContinue} busy={busy} error={error} />;
  }
  if (step === 'preview' && session) {
    return (
      <CallPreview
        session={session}
        callType={form.callType}
        joining={busy}
        onJoin={placeCall}
        onBack={() => { setStep('join'); setError(''); }}
      />
    );
  }
  if (step === 'calling' && session) {
    return <CallStage session={session} onEnded={onEnded} />;
  }

  return (
    <div className="gs-full">
      <div className="gs-join-card gs-msg">
        <Phone width={26} height={26} />
        <b>{endTitle(ending)}</b>
        <p>{endCopy(ending, session ? session.ownerName : 'them')}</p>
        {/* 🟥 M3: no way to try again — a missed, declined or failed call left a link that still
              works and a page that said it would not. 🟢 Back to the preview, same session. */}
        {canCallAgain(ending) && session && (
          <button type="button" className="gs-btn gs-btn-primary" onClick={() => { setEnding(null); setError(''); setStep('preview'); }}>
            Call again
          </button>
        )}
      </div>

      {/* ── how was it? ────────────────────────────────────────────────────
          🟢 THE SAME CARD THE SIGNED-IN WEB APP SHOWS, not a guest-shaped copy
             of it — same three steps, same routes, same server-side policy, so
             a guest's answer lands in `callRatings` beside everybody else's and
             is comparable with them.

          🟥 AND THE SERVER HAS TO WAIVE THE SAMPLING FOR A GUEST, which it does
             (see isGuestAccount in server/functions/callFeedback.js). The policy
             asks after ~12% of calls because an ordinary person makes thousands
             and the card has to stay worth reading. A guest makes exactly ONE,
             and then the account is deleted with the link — so sampling here
             does not spread the question out, it drops seven guests in eight and
             never gets another chance at them.

          🟢 `lastEnded` is only set for a call that connected and lasted, so a
             link that rang unanswered ends on the message above and nothing
             else — there is nothing to ask somebody about a call that never
             happened. */}
      {ending && ending.rate && (
        <CallRatingCard
          key={ending.rate.callId}
          call={ending.rate}
          onClose={closeRating}
        />
      )}
    </div>
  );
}
